/**
 * Client for the PUBLIC (unauthenticated) customer deposit endpoints (tv-k9b.8).
 *
 * The deposit pay page renders with NO staff login, so it loads + pays its one
 * deposit through these public routes rather than the authed /api/bootstrap +
 * pay-deposit intent (which 401 for a customer). It only "worked" locally because
 * the dev proxy injects a staff token into every /api call — that doesn't exist on
 * a real deploy, which is why the page was dead on staging.
 *
 * One clock (tv-2na.10.5): the pay sends the facilitator's sim clock as `nowIso`
 * (like api/intents.ts); the server honours it only in a demo/dev deployment.
 */
import { useSimulationStore } from '@/state/simulationStore';

/** A pay-over-time plan as the public page sees it (tv-2na.8.10): the schedule,
 *  what is still owed, and the instalment due now. */
export interface PublicTopupPlan {
  id: string;
  status: string;
  totalAud: number;
  paidAud: number;
  outstandingAud: number;
  nextDue: { dueIso: string; amountAud: number } | null;
  instalments: Array<{ dueIso: string; amountAud: number; status: string }>;
}

export interface PublicDepositBundle {
  nowIso?: string;
  deposit: {
    id: string;
    amountAud: number;
    /** Money already in (a part-paid deposit). */
    amountPaid?: number;
    /** What is still owed (amountAud - amountPaid) — what a plain link charges. */
    owedAud?: number;
    status: string;
    payAtStudio: boolean;
    expiresAt: string | null;
    /** B4 (7g3y): the link's state from the server's one lifecycle rule — expired from its pay-by, not the sweep. */
    linkState?: 'open' | 'expired' | 'on-file' | 'needs-date' | 'slot-taken';
    /** No slot held. An unpaid link like this can't be paid (linkState 'needs-date'). */
    dateless?: boolean;
    artistId: string | null;
    studioId: string;
  };
  booking: {
    id: string;
    start: string | null;
    sessionLength: string;
    durationMin: number;
    status: string;
  } | null;
  artistName: string | null;
  studioName: string | null;
  /** This deposit's OWN customer, for the page to pre-fill (D05-11). */
  customer: {
    firstName: string;
    lastName: string;
    phone: string | null;
    email?: string | null;
    /** An IG lead's `@handle` placeholder: not offered as their name. */
    placeholderName?: boolean;
  } | null;
  /** The deposit's pay-over-time plan, when it has one. */
  topupPlan?: PublicTopupPlan | null;
}

/** Load one deposit link's data. Returns null for a missing/expired-record 404,
 *  and throws only on a network/transport error so the page can retry. */
export async function fetchPublicDeposit(id: string): Promise<PublicDepositBundle | null> {
  const res = await fetch(`/api/public/deposit/${encodeURIComponent(id)}?nowIso=${encodeURIComponent(useSimulationStore.getState().clockIso)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`public-deposit ${res.status}`);
  return (await res.json()) as PublicDepositBundle;
}

export type PayPublicDepositResult =
  | { ok: true; alreadyPaid: boolean; bundle: PublicDepositBundle | null }
  | { ok: false; status: number; error: string };

/** Pay the deposit, capturing the customer's name, mobile and email (D05-11).
 *  Never throws — returns a discriminated result the page branches on. */
export async function payPublicDeposit(
  id: string,
  details: { name?: string; mobile?: string; email?: string },
): Promise<PayPublicDepositResult> {
  let res: Response;
  try {
    res = await fetch(`/api/public/deposit/${encodeURIComponent(id)}/pay`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...details, nowIso: useSimulationStore.getState().clockIso }),
    });
  } catch {
    return { ok: false, status: 0, error: 'network' };
  }
  if (res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      alreadyPaid?: unknown;
      bundle?: PublicDepositBundle | null;
    };
    return { ok: true, alreadyPaid: body.alreadyPaid === true, bundle: body.bundle ?? null };
  }
  const body = (await res.json().catch(() => undefined)) as { error?: string } | undefined;
  return { ok: false, status: res.status, error: body?.error ?? `http-${res.status}` };
}

export type PayPublicTopupResult =
  | { ok: true; bundle: PublicDepositBundle | null }
  | { ok: false; status: number; error: string };

/** Pay the instalment due now on the deposit's plan (tv-2na.8.10) — the SAME
 *  pay-topup transaction as the staff intent. Never throws. */
export async function payPublicTopup(
  id: string,
  details: { name?: string; mobile?: string; email?: string },
): Promise<PayPublicTopupResult> {
  let res: Response;
  try {
    res = await fetch(`/api/public/deposit/${encodeURIComponent(id)}/pay-topup`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...details, nowIso: useSimulationStore.getState().clockIso }),
    });
  } catch {
    return { ok: false, status: 0, error: 'network' };
  }
  if (res.ok) {
    const body = (await res.json().catch(() => ({}))) as { bundle?: PublicDepositBundle | null };
    return { ok: true, bundle: body.bundle ?? null };
  }
  const body = (await res.json().catch(() => undefined)) as { error?: string } | undefined;
  return { ok: false, status: res.status, error: body?.error ?? `http-${res.status}` };
}
